// The player's arsenal + shop data, ported from zombies/src/config.ts. Weapon RANGES are scaled to
// longfight's stud world (×RANGE = 15; zombies' ~100-unit ranges become ~1500 studs, in line with the
// NPC roles' 300–1150 stud engage ranges). Damage / fire-rate / mag / reload are scale-independent and
// kept as-is — they read sensibly against longfight HP (little 100, giant 550, tank 120–1440).

const RANGE = 15; // zombies world-units → studs

export interface WeaponDef {
  id: string;
  name: string;
  damage: number;
  fireRate: number; // rounds per minute
  magSize: number;
  reserveAmmo: number;
  reloadTime: number; // seconds
  range: number; // studs; beyond this a shot deals no damage
  automatic: boolean;
  pellets?: number; // >1 = shotgun: that many rays per shot
  spread?: number; // radians of cone half-angle
  viewColor: number; // procedural viewmodel tint
  infiniteReserve?: boolean; // the starter pistol never runs dry
  melee?: boolean; // left-click cone swing (no ammo); fireRate = swings/min
  deploy?: boolean; // left-click lobs an exploding entity; consumes 1 ammo
}

export const WEAPONS: Record<string, WeaponDef> = {
  pistol: {
    id: "pistol", name: "M1911", damage: 34, fireRate: 350, magSize: 8,
    reserveAmmo: 80, reloadTime: 1.4, range: 100 * RANGE, automatic: false, viewColor: 0x999999,
    infiniteReserve: true,
  },
  smg: {
    id: "smg", name: "MP5", damage: 24, fireRate: 750, magSize: 30,
    reserveAmmo: 240, reloadTime: 2.0, range: 90 * RANGE, automatic: true, viewColor: 0x6a6a72,
  },
  shotgun: {
    id: "shotgun", name: "BOOMSTICK", damage: 40, fireRate: 115, magSize: 8,
    reserveAmmo: 72, reloadTime: 2.1, range: 44 * RANGE, automatic: false,
    pellets: 12, spread: 0.1, viewColor: 0x7a5230,
  },
  rifle: {
    id: "rifle", name: "AK-74", damage: 40, fireRate: 600, magSize: 30,
    reserveAmmo: 270, reloadTime: 2.3, range: 120 * RANGE, automatic: true, viewColor: 0x4a4a3a,
  },
  sniper: {
    id: "sniper", name: "SNIPER", damage: 850, fireRate: 70, magSize: 6,
    reserveAmmo: 50, reloadTime: 2.3, range: 400 * RANGE, automatic: false, spread: 0.001, viewColor: 0x2a2a30,
  },
  minigun: {
    id: "minigun", name: "MINIGUN", damage: 17, fireRate: 1300, magSize: 150,
    reserveAmmo: 600, reloadTime: 4.5, range: 110 * RANGE, automatic: true, spread: 0.03, viewColor: 0x3a3a3a,
  },
  fastshotgun: {
    id: "fastshotgun", name: "AA-12", damage: 15, fireRate: 220, magSize: 8,
    reserveAmmo: 80, reloadTime: 2.2, range: 34 * RANGE, automatic: true, pellets: 6, spread: 0.08, viewColor: 0x5a4020,
  },
  flamethrower: {
    id: "flamethrower", name: "FLAMETHROWER", damage: 11, fireRate: 1000, magSize: 200,
    reserveAmmo: 800, reloadTime: 3.0, range: 15 * RANGE, automatic: true, pellets: 4, spread: 0.16, viewColor: 0xaa3311,
  },
  briefcase: {
    id: "briefcase", name: "Briefcase of Oysters", damage: 200, fireRate: 130, magSize: 0,
    reserveAmmo: 0, reloadTime: 0, range: 3.2 * RANGE, automatic: false, melee: true, viewColor: 0x6b4a2a,
  },
  chickensphere: {
    id: "chickensphere", name: "Chicken Spheres", damage: 150, fireRate: 150, magSize: 10,
    reserveAmmo: 0, reloadTime: 0, range: 100 * RANGE, automatic: false, deploy: true, viewColor: 0xd98a3a,
  },
};

export const STARTING_WEAPON = "pistol";

/** Knife-cone parameters reused by the briefcase melee (arc half-angle in radians). */
export const MELEE = { arc: 0.95 } as const;

/** Pack-a-Punch upgrade: applied to the player's CURRENT weapon (×1.6^level damage, +mag, +reserve). */
export const PACK_A_PUNCH = {
  damageMul: 1.6,
  magMul: 1.5,
  reserveMul: 1.5,
  nameSuffix: " +",
} as const;

/** Guns the (gun) Mystery Box can roll (excludes the starter pistol). */
export const MYSTERY_POOL = ["smg", "shotgun", "rifle", "sniper", "minigun", "fastshotgun", "flamethrower"];
/** Weapons the MELEE box can roll. */
export const MELEE_POOL = ["briefcase", "chickensphere"];
/** Named mystery pools, keyed by a station's refId. */
export const MYSTERY_POOLS: Record<string, string[]> = { guns: MYSTERY_POOL, melee: MELEE_POOL };

export interface PerkDef {
  id: string;
  name: string;
  effect: string;
  cost: number;
  color: number;
}

export const PERKS: Record<string, PerkDef> = {
  juggernog: { id: "juggernog", name: "Juggernog", effect: "+100 max health (stacks)", cost: 2500, color: 0xcc2222 },
  staminup: { id: "staminup", name: "Stamin-Up", effect: "+40% move speed", cost: 2000, color: 0xffd23f },
  speedcola: { id: "speedcola", name: "Speed Cola", effect: "faster reload (stacks)", cost: 3000, color: 0x33cc44 },
  doubletap: { id: "doubletap", name: "Double Tap", effect: "faster fire rate (stacks)", cost: 2000, color: 0xdd7722 },
  deadshot: { id: "deadshot", name: "Deadshot", effect: "+20% damage (stacks)", cost: 2500, color: 0x884466 },
  quickrevive: { id: "quickrevive", name: "Quick Revive", effect: "faster regen (stacks)", cost: 1500, color: 0x44aadd },
};

/** Perks that may be bought repeatedly to stack (Stamin-Up walkspeed cannot). */
export const STACKABLE_PERKS = new Set(["juggernog", "speedcola", "doubletap", "deadshot", "quickrevive"]);

export type StationKind = "perk" | "weapon" | "mystery" | "pap" | "maxammo";

export interface ShopStation {
  id: string;
  kind: StationKind;
  refId: string; // perk id / weapon id; for "mystery" the pool name; unused for pap/maxammo
  cost: number;
  color: number;
  label: string;
}

/** The stations inside the shop building. The Shop lays these out along the interior walls. */
export const SHOP_STATIONS: ShopStation[] = [
  { id: "jug", kind: "perk", refId: "juggernog", cost: 2500, color: 0xcc2222, label: "JUGGERNOG" },
  { id: "stam", kind: "perk", refId: "staminup", cost: 2000, color: 0xffd23f, label: "STAMIN-UP" },
  { id: "speed", kind: "perk", refId: "speedcola", cost: 3000, color: 0x33cc44, label: "SPEED COLA" },
  { id: "dtap", kind: "perk", refId: "doubletap", cost: 2000, color: 0xdd7722, label: "DOUBLE TAP" },
  { id: "dead", kind: "perk", refId: "deadshot", cost: 2500, color: 0x884466, label: "DEADSHOT" },
  { id: "qrev", kind: "perk", refId: "quickrevive", cost: 1500, color: 0x44aadd, label: "QUICK REVIVE" },
  { id: "buy-smg", kind: "weapon", refId: "smg", cost: 1200, color: 0x6a6a72, label: "MP5" },
  { id: "buy-sg", kind: "weapon", refId: "shotgun", cost: 1500, color: 0x7a5230, label: "BOOMSTICK" },
  { id: "buy-ak", kind: "weapon", refId: "rifle", cost: 3000, color: 0x4a4a3a, label: "AK-74" },
  { id: "box", kind: "mystery", refId: "guns", cost: 950, color: 0xc9a227, label: "MYSTERY BOX" },
  { id: "box-melee", kind: "mystery", refId: "melee", cost: 750, color: 0x7fd1c0, label: "MELEE BOX" },
  { id: "pap", kind: "pap", refId: "", cost: 5000, color: 0x9b59ff, label: "PACK-A-PUNCH" },
  { id: "ammo", kind: "maxammo", refId: "", cost: 1500, color: 0x33aaff, label: "MAX AMMO" },
];

/** Points economy: earned by damaging/killing RED units, spent at the shop. */
export const POINTS = {
  start: 1500, // stipend so you can buy something on your first trip
  hit: 10, // per damaging shot
  killLittle: 60,
  killGiant: 300,
  killTank: 200,
} as const;
